/**
 * Thin wrapper over the site's EXISTING analytics transport — Google Tag
 * Manager's `window.dataLayer` (GTM is installed in main.js via
 * @gtm-support/vue2-gtm). We do NOT add a second GA/GTM tag: every custom
 * event is pushed onto the same dataLayer, and the GA4 tag configured inside
 * the GTM container forwards it (GA4 transport uses sendBeacon, so events
 * survive an immediate page unload / navigation).
 *
 * All accessors are guarded so nothing throws in SSR, tests, or private-mode
 * browsers where storage is unavailable.
 */

/**
 * Returns the live dataLayer, creating it if GTM has not initialised yet.
 * Returns null only when there is no `window` at all.
 *
 * @returns {Array|null}
 */
export function getDataLayer() {
	if (typeof window === "undefined") {
		return null;
	}

	window.dataLayer = window.dataLayer || [];

	return window.dataLayer;
}

/**
 * Pushes a GA4-style custom event onto the dataLayer.
 *
 * @param {string} event event name, e.g. "migration_banner_click"
 * @param {Object} [params] additional GA4 event parameters
 * @returns {boolean} whether the push happened
 */
export function trackEvent(event, params) {
	if (!event) {
		return false;
	}

	const dataLayer = getDataLayer();

	if (!dataLayer) {
		return false;
	}

	dataLayer.push(Object.assign({ event }, params || {}));

	return true;
}

/**
 * @param {string} key sessionStorage key used as the once-per-session guard
 * @returns {boolean}
 */
export function hasFiredThisSession(key) {
	try {
		return sessionStorage.getItem(key) === "1";
	} catch (e) {
		return false;
	}
}

/**
 * @param {string} key sessionStorage key used as the once-per-session guard
 * @returns {boolean} whether the flag was written
 */
export function markFiredThisSession(key) {
	try {
		sessionStorage.setItem(key, "1");

		return true;
	} catch (e) {
		return false;
	}
}

/**
 * Fires an event at most once per browser session (sessionStorage-backed).
 *
 * @param {string} sessionKey guard key
 * @param {string} event event name
 * @param {Object} [params] GA4 event parameters
 * @returns {boolean} whether the event was fired on this call
 */
export function trackEventOncePerSession(sessionKey, event, params) {
	if (hasFiredThisSession(sessionKey)) {
		return false;
	}

	const fired = trackEvent(event, params);

	if (fired) {
		markFiredThisSession(sessionKey);
	}

	return fired;
}

/**
 * Whether a non-empty `user_id` has already been pushed onto the dataLayer
 * (e.g. by the GTM container's own config). Used so we never override an
 * existing user_id.
 *
 * @returns {boolean}
 */
export function analyticsUserIdPresent() {
	const dataLayer = getDataLayer();

	if (!dataLayer) {
		return false;
	}

	return dataLayer.some(
		(entry) =>
			entry &&
			typeof entry === "object" &&
			Object.prototype.hasOwnProperty.call(entry, "user_id") &&
			entry.user_id !== null &&
			entry.user_id !== undefined &&
			entry.user_id !== ""
	);
}

/**
 * Sets the GA4 `user_id` to the internal student id — but ONLY when the site's
 * existing analytics setup has not already set one (matching the new app,
 * which sets user_id to the same internal id). Never pass an email here.
 *
 * @param {string|number} userId internal student id
 * @returns {boolean} whether we set it (false = invalid id, or already set)
 */
export function setAnalyticsUserId(userId) {
	if (userId === null || userId === undefined || userId === "") {
		return false;
	}

	const dataLayer = getDataLayer();

	if (!dataLayer) {
		return false;
	}

	if (analyticsUserIdPresent()) {
		return false;
	}

	dataLayer.push({ user_id: String(userId) });

	return true;
}
